import { Link, Text } from "basis";
import { isVerificationCode } from "library/validator";
import { useEffect, useState } from "react";
import ReactOtpInput from "react-otp-input";
import { Loading } from "../Loading/Loading";
import { SvgInline } from "../SvgInline/SvgInline";
import styles from "./otp-input.module.scss";

const DEFAULT_NUM_INPUTS = 6;

export const OTP_ERRORS = {
  // FORBIDDEN is returned by api
  FORBIDDEN:
    "Code invalid. Please check the code we sent you or request a new code and try again.",
  CodeNot6Digits: `Code needs to be 6 digits`,
  CodeNot4Digits: "Code needs to be 4 digits",
  CodeEmpty: "Enter the code",
  Other:
    "Something's gone wrong. Sorry, we've encountered an unexpected problem.",
};

function OtpInput({
  numInputs = DEFAULT_NUM_INPUTS,
  errorCode,
  errorMessage,
  onChange = () => {},
  resendOtp = () => {},
  inputStyle,
  containerStyle,
  disabled,
  className,
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);
  const [showError, setShowError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resentCode, setResentCode] = useState(false);
  const [resentLoading, setResentLoading] = useState(false);

  useEffect(() => {
    const parentError = OTP_ERRORS[errorCode] || errorMessage || false;
    setError(parentError);
    setShowError(!!parentError);
  }, [errorCode, errorMessage]);

  const determineOtpError = (val, num) =>
    !isVerificationCode(val, num) && OTP_ERRORS[`CodeNot${num}Digits`];

  return (
    <div className={styles.wrapper}>
      <div
        className={`${styles.input} ${className} ${
          showError ? styles.inputError : ""
        }`}
        onFocus={() => setShowError(false)}
        onBlur={() => {
          const errorMsg = determineOtpError(value, numInputs);
          setError(errorMsg);
          setShowError(!!errorMsg);
          onChange({ value, success, error: errorMsg, showError: !!errorMsg });
        }}
      >
        <ReactOtpInput
          value={value}
          onChange={(val) => {
            setValue(val);
            const errorMsg = determineOtpError(val, numInputs);
            setError(errorMsg);
            setSuccess(!errorMsg);
            onChange({
              value: val,
              success: !errorMsg,
              error: errorMsg,
              showError,
            });
          }}
          inputStyle={inputStyle || { width: "48px" }}
          containerStyle={containerStyle}
          numInputs={numInputs}
          isDisabled={disabled}
        />
      </div>
      <div className={styles.resend}>
        {showError && (
          <div
            data-trackid="otp-input-error"
            className={`body02 ${styles.error}`}
          >
            {error}
          </div>
        )}
        <div className={`body02 ${styles.actionsBlock}`}>
          <div className={styles.resendBlock}>
            <Text>Didn’t get the code?</Text>
            {resentCode ? (
              <span className={styles.codeSent}>
                <SvgInline
                  className={styles.iconTick}
                  name="icon-tick"
                  strokeWhite
                />
                Code sent
              </span>
            ) : resentLoading ? (
              <span className={styles.resentLoading}>
                <Loading size="small" />
              </span>
            ) : (
              <button
                data-trackid="resend-code"
                disabled={resentLoading}
                type="button"
                className={styles.resendButton}
                onClick={async () => {
                  setResentLoading(true);
                  const err = {
                    hasError: false,
                    msg: "",
                  };
                  try {
                    await resendOtp();
                  } catch (e) {
                    const message =
                      e.response && e.response.data
                        ? e.response.data.message
                        : `Sorry, we've encountered an unexpected problem.`;

                    err.msg = message;
                    err.hasError = true;
                  }
                  setResentLoading(false);
                  setError(err.msg || determineOtpError(value, numInputs));
                  setShowError(err.hasError);
                  setResentCode(!err.hasError);
                  if (!err.hasError) {
                    setTimeout(() => setResentCode(false), 5000);
                  }
                }}
              >
                Resend code
              </button>
            )}
          </div>
          <Text>
            or{" "}
            <Link href="/contact" newTab={false}>
              contact us to change your number
            </Link>
          </Text>
        </div>
      </div>
    </div>
  );
}

export { OtpInput };
